import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

// Multi-script teleprompter. Plays a queue of scripts back-to-back, stopping
// between each with a "Restart this section" / "Continue → Next: [title]" overlay.
const Megaprompter = forwardRef(
  (
    {
      scripts = [],
      speed = 100,
      initialMarginTop = 0,
      paddingLeft = 10,
      paddingRight = 10,
      bottomMargin = 75,
      fontSize = 24,
      countdownInit = 5,
      startMode = "countdown",
      mirrorHorizontal = false,
      mirrorVertical = false,
      invertColors = false,
      onSectionDone,
    },
    ref
  ) => {
    const [index, setIndex] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [awaitingStart, setAwaitingStart] = useState(false);
    const [showBetweenScreen, setShowBetweenScreen] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const scrollRef = useRef(null);
    const containerRef = useRef(null);
    const countdownRef = useRef(null);

    const currentScript = scripts[index] ?? null;
    const nextScript = scripts[index + 1] ?? null;
    const isLast = index >= scripts.length - 1;

    useEffect(() => {
      const handler = () => setIsFullscreen(!!document.fullscreenElement);
      document.addEventListener("fullscreenchange", handler);
      return () => document.removeEventListener("fullscreenchange", handler);
    }, []);

    const goFullscreen = async () => {
      const el = containerRef.current;
      if (!el || document.fullscreenElement) return;
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;
      try {
        if (req) await req.call(el);
      } catch (_) {}
    };

    const exitFullscreen = () => {
      const fn =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;
      if (fn) fn.call(document);
    };

    const anchorToTop = () => {
      const el = scrollRef.current;
      if (!el) return;
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = mirrorVertical ? max : 0;
    };

    const beginCountdown = () => {
      countdownRef.current = countdownInit;
      setCountdown(countdownInit);
      setIsCountingDown(true);
    };

    // Reset all per-section state, then kick off the section based on the mode.
    const playSection = (useStartButtonGate) => {
      setIsScrolling(false);
      setIsCountingDown(false);
      setCountdown(null);
      setShowBetweenScreen(false);
      setScrollPosition(0);
      anchorToTop();
      requestAnimationFrame(anchorToTop);

      if (useStartButtonGate && startMode !== "countdown") {
        // Between-script Continue already acted as the gate. If countdown is
        // still required, do the countdown; otherwise jump straight to scrolling.
        if (startMode === "countdownAndStartbutton") {
          beginCountdown();
        } else {
          setIsScrolling(true);
        }
        return;
      }

      // Initial start of section 0 — honour startMode.
      if (startMode === "countdown") {
        beginCountdown();
      } else {
        setAwaitingStart(true);
      }
    };

    const start = async () => {
      await goFullscreen();
      setIndex(0);
      // Defer to next tick so index reset is reflected before scrolling
      requestAnimationFrame(() => playSection(false));
    };

    const handleInDisplayStart = () => {
      setAwaitingStart(false);
      if (startMode === "countdownAndStartbutton") {
        beginCountdown();
      } else {
        setIsScrolling(true);
      }
    };

    const handleContinue = () => {
      if (isLast) return;
      onSectionDone && onSectionDone(index);
      setIndex((i) => i + 1);
      requestAnimationFrame(() => playSection(true));
    };

    const handleRestartSection = () => {
      playSection(true);
    };

    const handleFinish = () => {
      onSectionDone && onSectionDone(index);
      setShowBetweenScreen(false);
      exitFullscreen();
    };

    const pause = () => setIsScrolling(false);
    const play = () => setIsScrolling(true);
    const toggle = () => setIsScrolling((s) => !s);

    const restart = () => {
      setIndex(0);
      setIsScrolling(false);
      setIsCountingDown(false);
      setCountdown(null);
      setAwaitingStart(false);
      setShowBetweenScreen(false);
      setScrollPosition(0);
      anchorToTop();
      requestAnimationFrame(anchorToTop);
    };

    useImperativeHandle(
      ref,
      () => ({ start, pause, play, toggle, restart, getState: () => ({ index, isScrolling }) }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [index, isScrolling, startMode, countdownInit, scripts.length]
    );

    // Countdown -> scroll
    useEffect(() => {
      if (!isCountingDown) return;
      const id = setInterval(() => {
        countdownRef.current -= 1;
        if (countdownRef.current <= 0) {
          clearInterval(id);
          setIsCountingDown(false);
          setCountdown(null);
          setIsScrolling(true);
        } else {
          setCountdown(countdownRef.current);
        }
      }, 1000);
      return () => clearInterval(id);
    }, [isCountingDown]);

    // Scrolling loop with end-of-script detection
    useEffect(() => {
      if (!isScrolling) return;
      const interval = setInterval(() => {
        const el = scrollRef.current;
        if (!el) return;
        const max = Math.max(0, el.scrollHeight - el.clientHeight);
        setScrollPosition((p) => {
          const next = p + 1;
          if (next >= max) {
            setIsScrolling(false);
            setShowBetweenScreen(true);
            return max;
          }
          return next;
        });
      }, speed);
      return () => clearInterval(interval);
    }, [isScrolling, speed]);

    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = mirrorVertical ? max - scrollPosition : scrollPosition;
    }, [scrollPosition, mirrorVertical]);

    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = mirrorVertical ? max : 0;
    }, [mirrorVertical, currentScript?.text, fontSize, bottomMargin]);

    const scrollAreaTransform = mirrorVertical ? "rotate(180deg)" : undefined;
    const contentTransform = mirrorVertical
      ? `rotate(180deg) ${mirrorHorizontal ? "scaleX(-1)" : ""}`.trim()
      : mirrorHorizontal
      ? "scaleX(-1)"
      : undefined;

    return (
      <div
        className="teleprompter-container"
        ref={containerRef}
        style={{
          position: "relative",
          backgroundColor: invertColors ? "#ffffff" : undefined,
          color: invertColors ? "#111827" : undefined,
        }}
      >
        {isFullscreen && (
          <button
            onClick={exitFullscreen}
            title="Exit fullscreen"
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 20,
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "none",
              cursor: "pointer",
              background: "rgba(0,0,0,0.5)",
              color: "#fff",
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        )}

        {/* Section position indicator */}
        {scripts.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 15,
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              padding: "4px 10px",
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            {index + 1} / {scripts.length}{currentScript?.title ? ` — ${currentScript.title}` : ""}
          </div>
        )}

        {/* Countdown overlay */}
        {isCountingDown && (
          <div style={overlayStyle}>
            <div style={{ fontSize: "6rem", color: "#fff", fontWeight: 700, lineHeight: 1 }}>
              {countdown}
            </div>
          </div>
        )}

        {/* In-display start button overlay (first section, startbutton modes) */}
        {awaitingStart && (
          <div style={{ ...overlayStyle, zIndex: 11 }}>
            <button onClick={handleInDisplayStart} style={bigButtonStyle("#038edc")}>
              Start
            </button>
          </div>
        )}

        {/* Between-script overlay */}
        {showBetweenScreen && (
          <div style={{ ...overlayStyle, zIndex: 12, flexDirection: "column", gap: 18 }}>
            <div style={{ color: "#fff", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
              {isLast
                ? "All sections done."
                : `Section finished. Next: ${nextScript?.title || `Section ${index + 2}`}`}
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
              <button onClick={handleRestartSection} style={bigButtonStyle("#6b7280")}>
                ↻ Restart this section
              </button>
              {isLast ? (
                <button onClick={handleFinish} style={bigButtonStyle("#038edc")}>
                  Finish and mark filmed
                </button>
              ) : (
                <button onClick={handleContinue} style={bigButtonStyle("#038edc")}>
                  Next and mark filmed
                </button>
              )}
            </div>
          </div>
        )}

        <div
          className="teleprompter-text"
          ref={scrollRef}
          style={{
            marginTop: `${initialMarginTop}px`,
            overflow: "auto",
            transform: scrollAreaTransform,
            transformOrigin: "center center",
            paddingBottom: "150px",
            paddingTop: "150px",
            paddingLeft: `${paddingLeft}px`,
            paddingRight: `${paddingRight}px`,
          }}
        >
          <div
            className="teleprompter-content"
            style={{ transform: contentTransform, transformOrigin: "center center" }}
          >
            {(currentScript?.text ?? "").split("\n").map((line, i) => (
              <p
                key={i}
                style={{
                  marginBottom: `${bottomMargin}px`,
                  fontSize: `${fontSize ?? 24}px`,
                }}
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

const overlayStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.65)",
  zIndex: 10,
};

const bigButtonStyle = (bg) => ({
  padding: "16px 32px",
  borderRadius: 12,
  border: "none",
  cursor: "pointer",
  background: bg,
  color: "#fff",
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 700,
  fontSize: 18,
  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
});

Megaprompter.displayName = "Megaprompter";
export default Megaprompter;
